import { sortBy } from 'lodash'
import ChatsRepository from '@/api/repositories/chats'

export const state = {
  // list of chat rooms with just their room name and ID
  chats: [],
  // messages are in this format { 1 (chatId): [id: 1, content: "Go home and drink, grandpa.", profileId: 5] }
  messages: {},
  // this is a dedicated profiles for chat module. Slightly different from the profiles module
  // format is the same as the profiles module -> { <profile_id>: { <profile_information> } }
  profiles: {},
}

export const getters = {
  chats(state, getters, rootState, rootGetters) {
    // Add name and hint to chats
    const extraChatProperties = chat => {
      if (chat.profile) {
        return {
          name: 'Section L Front-Desk',
          hint: 'Hello, please let us know how we can help you.',
          position: 2,
        }
      } else if (chat.announcement) {
        return {
          name: rootGetters['auth/isStaff']
            ? `${chat.property.acronym} Announcements`
            : 'Announcements',
          position: 1,
        }
      } else if (chat.property) {
        return {
          name: `${chat.property.acronym} Guests Chat (${
            chat.property.numGuests + chat.property.numStaffs
          })`,
          hint: 'This is a space for our guests to get a chance to know each other, ask questions, plan an outing together. Hope you have a wonderful experience, starting from here.\nPlease note that this chat will expire the moment you check-out.',
          position: 3,
        }
      }
    }
    const chats = [...state.chats].map(chat => {
      return { ...chat, ...extraChatProperties(chat) }
    })
    return sortBy(chats, ['position', 'name'])
  },

  messages(state, getters, rootState, rootGetters) {
    const currentProfileId = rootGetters['auth/currentProfile'].id
    return Object.keys(state.messages).reduce((messagesObj, chatId) => {
      // Fill each message with additional information to render them
      messagesObj[chatId] = state.messages[chatId].map(message => {
        // if (message) {
        return {
          ...message,
          mine: message.profileId === currentProfileId,
          profile: getters.profiles[message.profileId],
        }
        // }
      })
      return messagesObj
    }, {})
  },

  profiles(state) {
    return state.profiles
  },

  newMessagesCount(state) {
    return state.chats.reduce((sum, chat) => {
      sum += chat.notificationsCount
      return sum
    }, 0)
  },

  frontDeskChat(state, getters) {
    return getters.chats.find(chat => chat.name === 'Section L Front-Desk')
  },
}

export const mutations = {
  SET_CHATS(state, chats) {
    state.chats = chats
  },

  UPDATE_CHAT(state, { chat }) {
    const idx = state.chats.findIndex(c => c.id === chat.id)
    // idx >= 0 ? (state.chats[idx] = chat) : state.chats.push(chat)
    if (idx >= 0) state.chats[idx] = chat
  },

  UPDATE_LAST_MESSAGE(state, { message }) {
    const idx = state.chats.findIndex(c => c.id === message.chatId)
    if (
      // replace if chat is found, and
      // there are no messages in the chat, or the lastMessage is older than the new message
      idx >= 0 &&
      (!state.chats[idx].lastMessage ||
        new Date(state.chats[idx].lastMessage?.createdAt) <
          new Date(message.createdAt))
    ) {
      state.chats[idx].lastMessage = message
    }
  },

  SET_MESSAGES(state, { messages, chatId }) {
    state.messages[chatId] = messages
  },

  ADD_MESSAGE(state, { message }) {
    if (state.messages[message.chatId]) {
      const idx = state.messages[message.chatId].findIndex(
        m => m.id === message.id
      )
      idx >= 0
        ? (state.messages[message.chatId][idx] = message)
        : state.messages[message.chatId].push(message)
    } else {
      state.messages[message.chatId] = [message]
    }
  },

  UPDATE_PROFILES(state, { profiles }) {
    // map the object in profileId => profile format
    const profilesObject = {}
    profiles.forEach(profile => (profilesObject[profile.id] = profile))
    state.profiles = { ...state.profiles, ...profilesObject }
  },

  SET_PROFILES(state, { profiles }) {
    // map the object in profileId => profile format
    const profilesObject = {}
    profiles.forEach(profile => (profilesObject[profile.id] = profile))
    state.profiles = profilesObject
  },

  ADD_PROFILE(state, { profile }) {
    state.profiles[profile.id] = profile
  },
  RESET_PROFILES(state) {
    state.profiles = {}
  },
}

export const actions = {
  async fetchChats({ commit }, { search = null } = {}) {
    const res = await ChatsRepository.getChats({ search })
    const chats = res.data.chats
    commit('SET_CHATS', chats)

    chats.forEach(chat => {
      if (chat.lastMessage) {
        commit('ADD_MESSAGE', { message: chat.lastMessage })
      }
    })

    return chats
  },

  async fetchMessages({ commit }, { chatId, clearProfiles = false } = {}) {
    if (clearProfiles) commit('RESET_PROFILES')
    const res = await ChatsRepository.getChat({ chatId })
    const { messages, profiles, ...chat } = res.data

    commit('UPDATE_CHAT', { chat })
    commit(clearProfiles ? 'SET_PROFILES' : 'UPDATE_PROFILES', { profiles })
    commit('SET_MESSAGES', { messages, chatId })

    return { messages, profiles }
  },

  addMessage({ commit }, { message, profile = null } = {}) {
    if (profile) commit('ADD_PROFILE', { profile })
    commit('ADD_MESSAGE', { message })
    commit('UPDATE_LAST_MESSAGE', { message })
  },

  // === ActionCable === //
  async onReceived({ dispatch }, data) {
    if (data.error) {
      throw data
    } else {
      const { chatId, profile, message } = data
      await dispatch('addMessage', { chatId, message, profile })
    }
  },

  async sendMessage(_, { content }) {
    await this.$cable.perform({
      channel: 'ChatChannel',
      action: 'send_message',
      data: { content },
    })
  },

  async markAsRead(_, { messageId, chatId }) {
    await this.$cable.perform({
      channel: 'ChatChannel',
      action: 'mark_as_read',
      data: { messageId, chatId },
    })
  },
}
